<template>
  <button
    class="ln-btn ln-btn-primary ln-btn-xs"
    :title="title"
    @click="onClick"
    aria-label="Export to PDF"
  >
    <font-awesome-icon :icon="['fas','print']" aria-hidden="true" />
    <span class="sr-only">Export to PDF</span>
    <span class="ln-btn-text" v-if="label">{{ label }}</span>
    <span class="ln-btn-text" v-else>PDF</span>
  </button>
</template>

<script>
import { PdfTableExporter } from '@/Services/pdf/PdfTableExporter';
import { reportToDoc } from '@/Services/pdf/adapters/reportToDoc';

export default {
  name: 'ExportPdfButton',
  props: {
    tableRef: String,
    selector: String,
    selectors: Array,
    rows: Array,
    columns: Array,
    title: { type: String, default: 'Export to PDF' },
    label: { type: String, default: '' },
    filename: String,
    subtitle: String,
    action: { type: String, default: 'open' }, // 'open' | 'download' | 'print'
    // New: Report V1 preset + metadata
    preset: { type: String, default: 'reportV1' },
    meta: { type: Object, default: () => ({}) },
    // Whether to append a totals row for numeric-like columns (amount/total/value/vat...)
    showTotals: { type: Boolean, default: false },
    // Optional: allow explicit store injection for micro-mounts/tests
    storeOverride: { type: Object, default: null }
  },
  methods: {
    async onClick() {
      try {
        let { columns, rows } = PdfTableExporter.resolve(this, {
          tableRef: this.tableRef, selector: this.selector, selectors: this.selectors,
          rows: this.rows, columns: this.columns
        });
        if (!rows || !rows.length) {
          this.$utils && this.$utils.toast && this.$utils.toast('No data found to export', 'warning');
          return;
        }
        // Filter out UI-only columns like "Actions" and sync rows accordingly
        const normalizedCols = (columns || []).map(c => ({
          key: c.key || c.field || c.label,
          field: c.field || c.key || c.label,
          label: c.label || c.key || c.field || ''
        }));
        const keepIdx = normalizedCols
          .map((c, idx) => ({ idx, c }))
          .filter(x => String(x.c.label || x.c.field || x.c.key).toLowerCase() !== 'actions')
          .map(x => x.idx);
        const filteredColumns = keepIdx.map(i => normalizedCols[i]);
        let filteredRows = rows;
        // If rows are arrays (from native tables), convert them into objects keyed by filtered column fields
        if (Array.isArray(rows) && rows.length && Array.isArray(rows[0])) {
          filteredRows = rows.map(r => {
            const obj = {};
            keepIdx.forEach((srcIdx, outIdx) => {
              const col = filteredColumns[outIdx];
              const field = col.field || col.key || col.label;
              obj[field] = r[srcIdx];
            });
            return obj;
          });
        }
        const model = reportToDoc({ title: this.title || 'Report', columns: filteredColumns, rows: filteredRows, options: { subtitle: this.subtitle, showTotals: this.showTotals } });
        const filename = this.filename || (this.title ? `${this.title}.pdf` : 'report.pdf');

        // Resolve a Vuex store instance robustly to avoid `this.$store` undefined contexts
        const store = this.storeOverride
          || this.$store
          || (this.$root && this.$root.$store)
          || (this.$parent && this.$parent.$store)
          || (await import('@/store')).default;

        if (!store || typeof store.dispatch !== 'function') {
          console.error('[ExportPdfButton] Vuex store not available');
          this.$utils && this.$utils.toast && this.$utils.toast('Export failed: store unavailable', 'error');
          return;
        }

        const act = (this.action || 'open').toLowerCase();
        if (act === 'download') {
          await store.dispatch('pdf/download', { model, filename });
        } else if (act === 'open') {
          await store.dispatch('pdf/open', { model });
        } else if (act === 'print') {
          // pdfmake supports print(), but our Vuex facade currently exposes open/download.
          // Use open (browser print can be used from viewer), or extend facade later if needed.
          await store.dispatch('pdf/open', { model });
        } else {
          await store.dispatch('pdf/open', { model });
        }
      } catch (e) {
        console.error('PDF export failed', e);
        this.$utils && this.$utils.toast && this.$utils.toast('Unable to export PDF. Check connection and try again.', 'error');
      }
    }
  }
}
</script>

<style scoped>
/* L⚡t‑ning tokens; radius ≤ 5px per project cap */
.ln-btn {
  --h: 28px;
  --icon-size: 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: var(--h);
  padding: 0 12px;
  font-family: var(--font-family, Montserrat, sans-serif);
  font-weight: 500;
  font-size: 14px;
  color: var(--text-primary);
  background: var(--accent-primary);
  border: 1px solid transparent;
  border-radius: var(--radius-button, 5px);
  transition: 0.3s ease all;
}
.ln-btn:hover { filter: brightness(1.05); }
.ln-btn:focus { outline: 2px solid var(--accent-secondary); outline-offset: 2px; }
.ln-btn-primary { background: var(--accent-primary); color: #fff; border-color: transparent; }
.ln-btn-xs { --h: 28px; padding: 0 10px; }
.ln-btn-text { line-height: 1; }
/* Ensure icons fit comfortably inside the 28px button height */
.ln-btn svg { width: var(--icon-size); height: var(--icon-size); flex: 0 0 auto; display: inline-block; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
</style>
